/**
 * Search bar, goal selector, and sort control.
 *
 * The goal selector (spec section 15) picks a ranking metric for you, and the
 * explanation line always names the metric it chose — the mapping is never
 * hidden behind a friendly label.
 */

import { useEffect, useRef, useState } from 'react';
import { METRICS } from '@/lib/nutrition/calculations';
import { GOALS, goalById, type GoalId } from '@/lib/nutrition/scoring';
import { SORT_GROUP_LABELS, SORT_OPTIONS, sortOptionById, type SortGroup } from '@/lib/nutrition/ranking';

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

const EXAMPLES = ['chicken breast', 'greek yogurt', 'milk', 'protein bar', 'rice', 'eggs'];

export function SearchBar({
  value,
  onSearch,
  busy,
}: {
  value: string;
  onSearch: (query: string) => void;
  busy: boolean;
}): React.JSX.Element {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setDraft(value), [value]);

  return (
    <div>
      <form
        className="search-form"
        role="search"
        onSubmit={(e) => {
          e.preventDefault();
          const q = draft.trim();
          if (q.length > 0) onSearch(q);
        }}
      >
        <div className="search-input-wrap">
          <span className="search-icon" aria-hidden="true">
            ⌕
          </span>
          <label className="visually-hidden" htmlFor="search-input">
            Search for a food product
          </label>
          <input
            id="search-input"
            ref={inputRef}
            className="search-input"
            type="search"
            value={draft}
            placeholder="Search products, e.g. chicken breast"
            autoComplete="off"
            maxLength={120}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={busy || draft.trim().length === 0}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </form>

      <div className="example-chips">
        <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', alignSelf: 'center' }}>
          Try:
        </span>
        {EXAMPLES.map((example) => (
          <button
            key={example}
            type="button"
            className="chip"
            onClick={() => {
              setDraft(example);
              onSearch(example);
            }}
          >
            {example}
          </button>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Goal selector
// ---------------------------------------------------------------------------

export function GoalSelector({
  goal,
  onChange,
  activeSortLabel,
}: {
  goal: GoalId;
  onChange: (goal: GoalId) => void;
  activeSortLabel: string;
}): React.JSX.Element {
  const selected = goalById(goal);
  return (
    <section className="panel" aria-labelledby="goal-heading">
      <h2 className="panel-title" id="goal-heading">
        What are you optimizing for?
      </h2>
      <div className="goal-row" role="group" aria-labelledby="goal-heading">
        {GOALS.map((g) => (
          <button
            key={g.id}
            type="button"
            className="goal-btn"
            aria-pressed={g.id === goal}
            onClick={() => onChange(g.id)}
          >
            {g.label}
          </button>
        ))}
      </div>
      <p className="goal-explain">
        {selected.explanation} Currently ranking by <code>{activeSortLabel}</code>.
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sort
// ---------------------------------------------------------------------------

export function SortControl({
  sortId,
  onChange,
  onOpenFilters,
  activeFilterCount,
  resultCount,
}: {
  sortId: string;
  onChange: (id: string) => void;
  onOpenFilters: () => void;
  activeFilterCount: number;
  resultCount: number;
}): React.JSX.Element {
  const groups: SortGroup[] = ['value', 'nutrition', 'price', 'package'];
  const active = sortOptionById(sortId);

  return (
    <div className="controls">
      <label className="visually-hidden" htmlFor="sort-select">
        Sort results by
      </label>
      <select
        id="sort-select"
        className="control-select"
        value={sortId}
        onChange={(e) => onChange(e.target.value)}
      >
        {groups.map((group) => (
          <optgroup key={group} label={SORT_GROUP_LABELS[group]}>
            {SORT_OPTIONS.filter((o) => o.group === group).map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      <button type="button" className="btn" onClick={onOpenFilters}>
        Filters
        {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
      </button>

      {/* The active sorting criterion is always stated (section 13). */}
      <span className="active-sort" aria-live="polite">
        Sorted by <strong>{active.label}</strong>
        {active.metric !== 'valueScore' && ` (${METRICS[active.metric].formula})`}
      </span>

      <span className="result-count">
        {resultCount} result{resultCount === 1 ? '' : 's'}
      </span>
    </div>
  );
}
